'use strict';

function SalaryService($http, $interpolate, upload) {
  var round = {
    base: '/api/salary/round/',
    detail: $interpolate('/api/salary/round/{{id}}/'),
    detailRoute: $interpolate('/api/salary/round/{{id}}/{{detail}}/'),
  };

  var invite = {
    base: '/api/salary/invite/',
    detail: $interpolate('/api/salary/invite/{{id}}/'),
    detailRoute: $interpolate('/api/salary/invite/{{id}}/{{detail}}/'),
    'import': '/api/core/csv-parser/',
    asCoworker: '/api/salary/invites-as-coworker'
  };

  var comment = {
    base: '/api/salary/comment/',
    detail: $interpolate('/api/salary/comment/{{id}}/'),
    detailRoute: $interpolate('/api/salary/comment/{{id}}/{{comment}}/')
  };

  var questionnaire = {
    detail: $interpolate('/api/salary/questionnaire/{{token}}/'),
    detailRoute: $interpolate('/api/salary/questionnaire/{{token}}/{{detail}}/')
  };

  var distribution = {
    base: '/api/salary/round-distribution/',
    detail: $interpolate('/api/salary/round-distribution/{{id}}/'),
    detailRoute: $interpolate('/api/salary/round-distribution/{{id}}/{{detail}}/')
  };

  var answer = $interpolate('/api/salary/answer/{{id}}/');

  return {
    list: {
      round: listRound,
      invite: listInvite,
      invitesAsCoworker: listInvitesAsCoworker
    },

    retrieve: {
      questionnaire: retrieveQuestionnaire,
      summary: retrieveSummary,
      basis: retrieveBasis,
      distribution: retrieveDistribution,
      automaticDistribution: retrieveAutomaticDistribution,
      intro: retrieveIntro
    },

    create: {
      round: createRound,
      invite: createInvite,
      invitations: createInvitations,
      inviteTokens: createInviteTokens,
      comment: createComment,
    },

    update: {
      round: updateRound,
      invite: updateInvite,
      answer: updateAnswer,
      summary: updateSummary,
      distribution: updateDistribution
    },

    'delete': {
      invite: deleteInvite,
      round: deleteRound
    },

    util: {
      inviteResend: inviteResend,
      'import': importInvites
    },

    urls: {
      invite: {
        'base':   $interpolate('/salary/{{round}}/invite'),
        'import': $interpolate('/salary/{{round}}/invite/import')
      },

      basis: {
        base: $interpolate('/salary/{{round}}/basis')
      },

      distribution: {
        base: $interpolate('/salary/{{round}}/distribution'),
        'export': $interpolate('/api/salary/round-distribution/{{id}}/export_payment_increase/')
      },

      questionnaire: {
        base: $interpolate('/salary/questionnaire/{{token}}'),
        step: $interpolate('/salary/questionnaire/{{token}}/{{step}}')
      }
    }
  };

  function listRound() {
    return $http.get(round.base);
  }

  function listInvitesAsCoworker() {
    return $http.get(invite.asCoworker);
  }

  function createRound(questionnaire, description, performanceReviewRound) {
    var data = { questionnaire: questionnaire, description: description, default_assessment_round_connection: performanceReviewRound };
    return $http.post(round.base, data);
  }

  function listInvite(id) {
    var params = { round: id };
    return $http.get(invite.base, { params: params });
  }

  function retrieveQuestionnaire(token, step) {
    var params = { step: step };
    return $http.get(questionnaire.detail({token: token}), { params: params });
  }

  function retrieveSummary(token) {
    return $http.get(questionnaire.detailRoute({token:token, detail: 'summary'}));
  }

  function retrieveBasis(id) {
    return $http.get(round.detailRoute({id: id, detail: 'basis'}));
  }

  function retrieveDistribution(id) {
    return $http.get(distribution.detail({id: id}));
  }

  function retrieveAutomaticDistribution(id) {
    return $http.get(distribution.detailRoute({id: id, detail: 'automatic_distribution'}));
  }

  function retrieveIntro(token) {
    return $http.get(questionnaire.detailRoute({token:token, detail: 'intro'}));
  }

  function createInvite(id, first_name, last_name, email, transpire) {
    return $http.post(invite.base, {
      round: id,
      first_name: first_name,
      last_name: last_name,
      email: email,
      transpire: new Date(transpire).toISOString(),
    });
  }

  function createInvitations(id, transpire, users) {
    var invites = (users || []).map(function(user) {
      user.transpire = new Date(transpire).toISOString();
      return user;
    });

    return $http.post(invite.detailRoute({ id: id, detail: 'import' }), invites);
  }

  function createInviteTokens(id, subject, body, signature) {
    return $http.post(invite.detailRoute({ id: id, detail: 'invite' }), {
      subject: subject, message: body, signature: signature
    });
  }

  function createComment(summaryComment, token) {
    return $http.post(comment.base, {
      comment: summaryComment,
      token: token,
    });
  }

  function updateRound(id, questionnaire, description, performanceReviewRound) {
    var update = { questionnaire: questionnaire, description: description, default_assessment_round_connection: performanceReviewRound };
    return $http({ url: round.detail({ id: id }), method: 'PATCH', data: update });
  }

  function updateInvite(id, first_name, last_name, transpire, subject, body, signature, assessmentRoundBasis, email) {
    var update = {
      first_name: first_name,
      last_name: last_name,
      transpire: new Date(transpire).toISOString(),
      subject: subject,
      message: body,
      signature: signature,
      assessment_round_connection: assessmentRoundBasis
    };
    if (email) {
      update['email'] = email;
    }
    return $http({ url: invite.detail({ id: id }), method: 'PATCH', data: update });
  }

  function updateAnswer(id, choice, comment) {
    var update = { choice: choice, comment: comment };
    return $http({ url: answer({id: id}), method: 'PATCH', data: update});
  }

  function deleteInvite(id) {
    return $http.delete(invite.detail({ id: id }));
  }

  function deleteRound(id) {
    return $http.delete(round.detail({ id: id }));
  }

  function inviteResend(id, subject, body, signature) {
    return $http.post(invite.detailRoute({ id: id, detail: 'resend_invite' }), {
      subject: subject, message: body, signature: signature
    });
  }

  function importInvites(file) {
    return upload({ url: invite.import, method: 'POST', data: { file: file } });
  }

  function updateSummary(token) {
    return $http({ method: 'PATCH', url: questionnaire.detail({token:token}), data: { completed: true } });
  }

  function updateDistribution(id, managerSum, employeeSums) {
    var data = { 'manager_sum': managerSum, 'employee_sums': employeeSums };
    return $http({ url: distribution.detail({id: id}), method: 'PATCH', data: data});
  }
}

module.exports = SalaryService;



/*****************
 ** WEBPACK FOOTER
 ** ./src/app/salary/service/salary/index.js
 ** module id = 206
 ** module chunks = 0
 **/